import { useState } from 'react';
import { Modal, Alert, Input, Typography, Button } from '../../ui';

export type CancelBreakdown = { ordered: number; packed: number; dispatched: number; delivered: number; pending: number };

type Props = {
  open: boolean;
  onClose: () => void;
  orderNo: string;
  breakdown: CancelBreakdown;
  fmtQty: (n: any) => string;
  /** No goods have shipped out yet → the whole order can still be withdrawn. */
  canFullCancel: boolean;
  /** Nothing shipped, but a recorded Tally invoice blocks the withdrawal until it's voided. */
  fullCancelBlockedByInvoice?: boolean;
  onFullCancel: () => void;
  fullCancelLoading?: boolean;
  /** ORDER.CANCEL holder (retailer/sales/admin) + feature on + not blocked by a whole-order invoice. */
  canShortClose: boolean;
  onShortClose: (reason: string) => void;
  shortCloseLoading?: boolean;
  /** When short-close isn't available but units are still pending — the reason to show instead of the button. */
  shortCloseHint?: { message: string; description: string };
};

const n = (v: any) => Math.round((Number(v) || 0) * 1000) / 1000;

/**
 * Cancel confirmation for a (possibly partially fulfilled) order. Instead of a bare "are you sure",
 * it shows the fulfilment breakdown and steers the user to the only safe action: a full withdrawal
 * when nothing has shipped, or short-closing just the pending remainder once goods are out the door
 * (dispatched/delivered units can never be cancelled here).
 */
export default function CancelOrderModal(p: Props) {
  const [reason, setReason] = useState('');
  const { breakdown: b, fmtQty } = p;
  const shippedOut = n(b.dispatched + b.delivered);
  const pending = n(b.pending);
  const isPartial = shippedOut > 1e-6;

  const stat = (label: string, value: number, color?: string) => (
    <div style={{ minWidth: 88 }} key={label}>
      <div style={{ fontSize: 12, color: '#8c8c8c' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, color }}>{fmtQty(value)}</div>
    </div>
  );

  let primary: JSX.Element | null = null;
  if (!isPartial && p.canFullCancel) {
    primary = <Button key="do" variant="danger" loading={p.fullCancelLoading} onClick={p.onFullCancel}>Cancel entire order</Button>;
  } else if (isPartial && pending > 1e-6 && p.canShortClose) {
    primary = <Button key="do" variant="danger" loading={p.shortCloseLoading} onClick={() => p.onShortClose(reason)}>Short-close {fmtQty(pending)} pending</Button>;
  }

  return (
    <Modal
      open={p.open}
      onOpenChange={(o) => { if (!o) p.onClose(); }}
      title={`Cancel order ${p.orderNo}`}
      width={560}
      footer={[<Button key="close" variant="default" onClick={p.onClose}>{primary ? 'Keep order' : 'Close'}</Button>, primary]}
    >
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid #f0f0f0' }}>
        {stat('Ordered', b.ordered)}
        {b.dispatched > 1e-6 && stat('Dispatched', b.dispatched, '#d46b08')}
        {b.delivered > 1e-6 && stat('Delivered', b.delivered, '#389e0d')}
        {b.packed > 1e-6 && stat('Packed (awaiting dispatch)', b.packed, '#d48806')}
        {stat('Pending', b.pending, '#1677ff')}
      </div>

      {isPartial ? (
        <>
          <Alert
            type="warning"
            style={{ marginBottom: 10 }}
            title={`${fmtQty(shippedOut)} unit(s) already dispatched — these cannot be cancelled.`}
            description="Goods that have left the warehouse can't be withdrawn. Only the units still pending (not yet dispatched) can be dropped."
          />
          {pending > 1e-6 ? (
            p.canShortClose ? (
              <>
                <Alert
                  type="info"
                  style={{ marginBottom: 10 }}
                  title={`Short-close the ${fmtQty(pending)} pending unit(s)?`}
                  description="They're removed from the order and the order total is revised to what was actually fulfilled. Settle any advance excess in Accounts (refund or keep as credit)."
                />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>Reason (optional)</Typography.Text>
                <Input.TextArea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. retailer no longer needs the remaining units" style={{ marginTop: 4 }} />
              </>
            ) : p.shortCloseHint ? (
              <Alert type="info" title={p.shortCloseHint.message} description={p.shortCloseHint.description} />
            ) : null
          ) : (
            <Alert type="info" title="Everything ordered has been shipped — there's nothing left to cancel." />
          )}
        </>
      ) : p.canFullCancel ? (
        <Alert
          type="warning"
          title="Cancel the whole order?"
          description={b.packed > 1e-6
            ? `This order hasn't been dispatched yet. Cancelling withdraws the entire order and returns the ${fmtQty(b.packed)} packed unit(s) to stock. This cannot be undone.`
            : "This order hasn't been packed or dispatched yet. Cancelling withdraws the entire order. This cannot be undone."}
        />
      ) : p.fullCancelBlockedByInvoice ? (
        <Alert
          type="info"
          title="Void the tax invoice before cancelling."
          description="A Tally tax invoice has been recorded for this order. Void it in Billing (Documents tab) to reverse the ledger, then cancel the order."
        />
      ) : (
        <Alert type="info" title="This order can't be cancelled from here." />
      )}
    </Modal>
  );
}
